import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getAccount, updateAccount } from "./api";
import { useAuth } from "./AuthContext";
import { promptPush, osSyncTags } from "./onesignal";
import { CHIPS, tagsFrom } from "../components/account/NotificationPrefs";

// Notifications access toggle: ticking a team = ALL its alerts on. Reads/writes the
// SAME account.push_prefs the My Account › Notifications page uses.
export function useNotifPick() {
    const { user } = useAuth();
    const qc = useQueryClient();
    const { data: account } = useQuery({ queryKey: ["account"], queryFn: getAccount, enabled: !!user });
    const prefs = account?.profile?.push_prefs || {};
    const sufs = (lg) => CHIPS.filter((c) => c[3] === "both" || c[3] === lg).map((c) => c[0]);
    const isOn = (lg, id) => sufs(lg).some((s) => prefs[`team_${lg}_${id}_${s}`] === true);
    const allOn = (lg, ids) => ids.length > 0 && ids.every((id) => isOn(lg, id));
    // Turning ON (a team, league, conference or division) first asks WHICH alerts via the
    // global NotifTopicsDialog; it calls back with `only` = the chosen topic suffixes.
    const setMany = async (lg, ids, on, only = null) => {
        if (on && !only) { window.dispatchEvent(new CustomEvent("notif-topics-pick", { detail: { lg, ids } })); return; }
        if (on && !only.length) on = false;
        const next = { ...prefs };
        ids.forEach((id) => sufs(lg).forEach((s) => { next[`team_${lg}_${id}_${s}`] = on && (!only || only.includes(s)); }));
        if (on && next.push_enabled !== true) { if (prefs.app_device || await promptPush()) next.push_enabled = true; }
        qc.setQueryData(["account"], (a) => ({ ...(a || {}), profile: { ...((a || {}).profile || {}), push_prefs: next } }));
        osSyncTags(tagsFrom(next));
        updateAccount({ push_prefs: next }).catch(() => toast.error("Couldn't save notification settings"));
    };
    const toggle = (lg, id) => setMany(lg, [id], !isOn(lg, id));
    const count = (lg, ids) => ids.filter((id) => isOn(lg, id)).length;
    return { enabled: !!user, isOn, allOn, setMany, toggle, count, sufs };
}
